import React, { useEffect, useState } from 'react';
import { crm } from './crmApi';
import type { CrmAuth, CrmLead } from './crmApi';
import LeadPanel from './LeadPanel';
import { LeadTypeChip, Modal, StageChip, fmtHm, fmtPhone, fmtWhen, goldBg, useCrmMe } from './ui';
import { greeting } from './viz';

// ── The day's first pop-up ───────────────────────────────────
// Once a day, when a salesperson opens the CRM: the polls to update,
// follow-ups gone late, today's follow-ups and the new leads to call (not on
// a holiday). Remembered per person in this browser; mirrors the app's showDayStart.

const seenKey = (who: string) => `vt_crm_daystart_${who}`;
const istToday = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
const readSeen = (who: string) => { try { return localStorage.getItem(seenKey(who)) || ''; } catch { return ''; } };
const markSeen = (who: string, day: string) => { try { localStorage.setItem(seenKey(who), day); } catch { /* storage blocked */ } };

type DayData = Awaited<ReturnType<typeof crm.dayStart>>;

const Rows: React.FC<{ leads: CrmLead[]; onOpen: (id: string) => void }> = ({ leads, onOpen }) => (
  <ul className="divide-y divide-gray-100">
    {leads.map(l => (
      <li key={l.id}>
        <button onClick={() => onOpen(l.id)} className="w-full text-left py-2 flex items-center gap-2 hover:bg-purple-50/50 rounded-lg px-1">
          <span className="flex-1 min-w-0">
            <span className="block text-sm font-semibold text-gray-800 truncate">{l.name || fmtPhone(l.phone)}</span>
            <span className="flex items-center gap-1.5 mt-0.5"><StageChip stage={l.stage} small /><LeadTypeChip type={l.leadType} /></span>
          </span>
          <span className={`text-xs font-bold whitespace-nowrap ${l.nextAt && new Date(l.nextAt).getTime() < Date.now() ? 'text-red-600' : 'text-gray-700'}`}>{fmtWhen(l.nextAt)}</span>
        </button>
      </li>
    ))}
  </ul>
);

const DayStart: React.FC<{ auth: CrmAuth; who: string; onNavigate: (page: string) => void }> = ({ auth, who, onNavigate }) => {
  const { me } = useCrmMe(auth);
  const [d, setD] = useState<DayData | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    if (readSeen(who) === istToday()) return;
    let live = true;
    crm.dayStart(auth).then(r => {
      // A holiday: nothing to start (it shows if the holiday is taken back)
      if (!live || r.holiday?.today) return;
      markSeen(who, istToday());
      if (r.answers.length || r.late.length || r.todayFollowUps.length || r.fresh) setD(r);
    }).catch(() => { /* the pages show their own errors */ });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [who]);

  if (open && me) return <LeadPanel auth={auth} leadId={open} me={me} onClose={() => setOpen(null)} onChanged={() => {}} />;
  if (!d) return null;
  const close = () => setD(null);
  const go = (page: string) => { close(); onNavigate(page); };
  const openLead = (id: string) => { close(); setOpen(id); };
  const section = (title: string, sub: string, leads: CrmLead[], tone: string) => leads.length > 0 && (
    <div className={`rounded-xl border p-3 ${tone}`}>
      <p className="text-sm font-bold text-gray-900">{title} <span className="text-gray-500 font-semibold">· {leads.length}</span></p>
      <p className="text-[11px] text-gray-500 mb-1">{sub}</p>
      <Rows leads={leads.slice(0, 8)} onOpen={openLead} />
      {leads.length > 8 && <p className="text-[11px] text-gray-400 mt-1">and {leads.length - 8} more in Follow-ups</p>}
    </div>
  );
  return (
    <Modal title={`${greeting()}, ${(d.name || '').split(' ')[0]}`} sub="Your day at a glance" onClose={close} wide>
      <div className="space-y-3">
        {d.shift?.thisWeek && <p className="text-sm text-gray-700">Your shift today: <b>{d.shift.thisWeek.name}</b>, {fmtHm(d.shift.thisWeek.start)} – {fmtHm(d.shift.thisWeek.end)}.</p>}
        {section('Polls to update', 'You sent them the poll. Update what they said: Interested, Not interested or No response.', d.answers, 'border-fuchsia-200 bg-fuchsia-50/60')}
        {section('Follow-ups gone late', 'Their time has passed. Call them first.', d.late, 'border-red-200 bg-red-50/60')}
        {section("Today's follow-ups", 'Call backs, payment dates, future prospects and visits booked for today.', d.todayFollowUps, 'border-gray-200 bg-white')}
        {d.fresh > 0 && (
          <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-3 flex items-center gap-3">
            <p className="text-3xl font-bold text-gray-900">{d.fresh}</p>
            <p className="text-sm text-gray-700"><b>new lead{d.fresh === 1 ? '' : 's'} to call.</b> Not picked, a lead goes to the next salesperson after your 3 tries or when your shift ends.</p>
          </div>
        )}
        <div className="flex flex-wrap gap-2 pt-1">
          <button onClick={() => go('today')} className="flex-1 min-w-[140px] py-2.5 rounded-xl text-sm font-bold text-white" style={goldBg}>Start calling</button>
          <button onClick={() => go('followups')} className="flex-1 min-w-[140px] py-2.5 rounded-xl text-sm font-bold border-2 border-gray-200 text-gray-700 hover:bg-gray-50">Open Follow-ups</button>
          <button onClick={close} className="px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-500 hover:bg-gray-50">Close</button>
        </div>
      </div>
    </Modal>
  );
};

export default DayStart;
